import moment from 'moment';

/**
 * Format date to the US date format
 * @param {Date} date date to format
 * @param {string} format template format
 * @returns {string} new date with format as string
 */
export const customDateFormatter = (date, format) => {
  const currentFormat = 'M/D/YY';
  let stringDate = '';

  if (date instanceof Date) {
    // Transform date to format: 2022-02-10T00:44:34.587Z
    stringDate = date.toISOString();
  } else if (typeof date === 'string') {
    stringDate = date;
  }
  // Replace hyphens by slash in order to interpretate date in UTC and avoid day off
  const dateFixed = new Date(stringDate.replace(/-/g, '/').replace(/T.+/, ''));
  return moment(dateFixed).format(format || currentFormat);
};

export const getLocalDate = (date) => {
  // when clear input the date is null, newDate(null) is Dec 31 1969, ternary and return avoid unwanted Dec 31 1969 in the date field
  const d = date instanceof Date ? date : date !== null && new Date(date);
  return d ? d.toLocaleDateString('en-CA') : '';
};

export const formatShortDate = (date) => {
  return customDateFormatter(date, 'MMM D, YYYY');
};

export const formatLongDate = (date) => {
  return customDateFormatter(date, 'MMMM D, YYYY');
};

export const formatNumericDate = (date) => {
  return customDateFormatter(date, 'MM/DD/YY');
};

/**
 * Format a site's `last_visit` for display: the date plus whether that visit was completed or only attempted.
 * @param {{date: string, completed: boolean}} lastVisit site.last_visit, or null when the site has no visits
 * @returns {string} e.g. "03/31/26 (attempted)", or '' when there is no visit to show
 */
export const formatLastVisit = (lastVisit) => {
  if (!lastVisit?.date) {
    return '';
  }
  const status = lastVisit.completed ? 'completed' : 'attempted';
  return `${formatNumericDate(lastVisit.date)} (${status})`;
};

export const formatShortDateWithTimeZone = (date) => {
  return moment(date).format('lll');
};

export const formatVisitAndNoteDate = (date) => {
  return getLocalDate(date);
};

export const formatTimestampDate = (date) => {
  return customDateFormatter(date, 'YYYY-MM-DD');
};

export const formatImportDate = (date) => {
  return customDateFormatter(date, 'MM/DD/YYYY');
};

export const formatImportDateTime = (date) => {
  if (!date) return '';

  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(d);

  const get = (type) => parts.find(p => p.type === type)?.value;

  const mm = get('month');
  const dd = get('day');
  const yyyy = get('year');
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = (get('dayPeriod') || '').toLowerCase();
  const tz = get('timeZoneName');

  return `${mm}/${dd}/${yyyy} at ${hour}:${minute}${dayPeriod} (${tz})`;
};

export const formatDateTimeInET = (date) => {
  if (!date) return '';

  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';

  // Example: "11/10/25 11:00am EST" (or EDT depending on date)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(d);

  const get = (type) => parts.find(p => p.type === type)?.value;

  const mm = get('month');
  const dd = get('day');
  const yy = get('year');
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = (get('dayPeriod') || '').toLowerCase();
  const tz = get('timeZoneName'); // "EST" or "EDT"

  return `${mm}/${dd}/${yy} ${hour}:${minute}${dayPeriod} ${tz}`;
};

/**
 * Check if date is valid
 * @param {} str Date
 * @returns {boolean} true/false
 */
export const isDateValid = (str) => {
  if (!str) {
    return false;
  }
  return moment(new Date(str)).isValid();
};

/*
  Compare two dates if isSame or before
  */
export const areDatesSameOrBefore = (date1, date2) => {
  return moment(date1).isSameOrBefore(date2);
};

/**
 * Check if pair of dates are in range
 * A [ indicates inclusion of a value.
 * A ( indicates exclusion of a value.
 * @param {*} startDate start date
 * @param {*} endDate end date
 * @returns boolean
 */
export const isDateInRange = (startDate, endDate) => {
  return moment(startDate).isBetween(startDate, endDate, null, '[]');
};

/**
 * Function to convert seconds to minutes
 */
export const getTimeInHoursAndMins = (seconds) => {
  const d = Number(seconds);
  const hour = Math.floor(d / 3600);
  const minutes = Math.floor((d % 3600) / 60);
  const hourDisplay = hour > 0 ? `${hour}hr` : '';
  const minutesDisplay = minutes > 0 ? `${minutes}min` : '';
  return `${hourDisplay} ${minutesDisplay}`;
};
